/**
 * The course builder's two hooks (docs/plans/lms-course-builder-plan.md 4.9,
 * 4.10, G20; WP-B30):
 *
 * - useBuilderAvailability(): GET /api/lms/health once per mount; the builder
 *   is offered only when health.builder?.enabled === true AND
 *   network_allowed === true (WP-B04: the block is optional, so a response
 *   from an older api reads as "no builder"; a failed read reads the same).
 *   Every entry point renders nothing until this says yes (G20).
 * - useBuildRun(runId): GET /admin/builder/runs/:id, then again every 5
 *   seconds while the run is not terminal (or until `stopWhen` says so) AND
 *   the page is visible (document.visibilityState, the LmsProvider summary
 *   poll's rule, LMS plan D14): a hidden tab asks nothing and a tab that
 *   becomes visible asks at once. Polling, never SSE (4.10).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getLmsHealth } from '../../../../api/client';
import { getBuilderRun, isTerminalRunStatus, readBuilderError } from '../../../../api/builder';
import type { BuilderApiError, BuilderRunDetail, LmsBuilderHealth } from '../../../../api/builder';

/** 4.10: the wizard and the run page poll a live run every 5 seconds. */
export const BUILD_RUN_POLL_MS = 5000;

export interface BuilderAvailability {
  /** false until the health read has answered (or failed). */
  checked: boolean;
  /** true only when the switch is on and the caller's network is allowed. */
  available: boolean;
  health: LmsBuilderHealth | null;
}

/** The builder block of GET /health read once; see the file header. */
export function useBuilderAvailability(): BuilderAvailability {
  const [state, setState] = useState<BuilderAvailability>({ checked: false, available: false, health: null });
  useEffect(() => {
    let cancelled = false;
    getLmsHealth()
      .then((health) => {
        if (cancelled) return;
        const builder = health && health.builder ? health.builder : null;
        const available = Boolean(builder && builder.enabled === true && builder.network_allowed === true);
        setState({ checked: true, available, health: builder });
      })
      .catch(() => {
        if (!cancelled) setState({ checked: true, available: false, health: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export interface UseBuildRunOptions {
  /** Poll interval in ms (default 5000). */
  intervalMs?: number;
  /** Stop polling once this returns true for the latest detail (the terminal statuses always stop it). */
  stopWhen?: (detail: BuilderRunDetail) => boolean;
  /** false: ask nothing (default true). */
  enabled?: boolean;
}

export interface BuildRunState {
  detail: BuilderRunDetail | null;
  /** The last read's failure, or null; a failure never stops the poll of a live run. */
  error: BuilderApiError | null;
  /** true until the first read has answered. */
  loading: boolean;
  /** Ask again now (after Start, Cancel or Retry). */
  refresh: () => void;
}

/** One run, polled while it is live; see the file header. */
export function useBuildRun(runId: string | null | undefined, options: UseBuildRunOptions = {}): BuildRunState {
  const { intervalMs = BUILD_RUN_POLL_MS, stopWhen, enabled = true } = options;
  const [detail, setDetail] = useState<BuilderRunDetail | null>(null);
  const [error, setError] = useState<BuilderApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(runId) && enabled);
  const [tick, setTick] = useState(0);
  const [visible, setVisible] = useState<boolean>(pageVisible);
  const stopRef = useRef(stopWhen);
  stopRef.current = stopWhen;
  const latestRef = useRef<BuilderRunDetail | null>(null);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => setVisible(pageVisible());
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // A new run id starts from nothing.
  useEffect(() => {
    latestRef.current = null;
    setDetail(null);
    setError(null);
    setLoading(Boolean(runId) && enabled);
  }, [runId, enabled]);

  useEffect(() => {
    if (!runId || !enabled || !visible) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const settled = (d: BuilderRunDetail | null): boolean => {
      if (!d || !d.run) return false;
      if (isTerminalRunStatus(d.run.status)) return true;
      return typeof stopRef.current === 'function' ? stopRef.current(d) === true : false;
    };

    const read = async () => {
      try {
        const next = await getBuilderRun(runId);
        if (cancelled) return;
        latestRef.current = next;
        setDetail(next);
        setError(null);
        setLoading(false);
        if (!settled(next)) timer = setTimeout(read, intervalMs);
      } catch (err) {
        if (cancelled) return;
        const problem = readBuilderError(err, 'The run could not be read.');
        setError(problem);
        setLoading(false);
        // A run that is gone, or a switch that is off, is not asked again; a blip is.
        const final = problem.status === 404 || problem.status === 403 || problem.code === 'LMS_BUILDER_DISABLED';
        if (!final && !settled(latestRef.current)) timer = setTimeout(read, intervalMs);
      }
    };

    // A run already settled is read once more only on an explicit refresh.
    void read();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, enabled, visible, intervalMs, tick]);

  return { detail, error, loading, refresh };
}
